/**
 * Today's Metrics Component
 * Shows key performance indicators for today
 */
import { Truck, Package, CheckCircle, AlertTriangle, TrendingUp, TrendingDown, Loader } from 'lucide-react';

const TodaysMetrics = ({ data, isFullScreen }) => {
  if (!data) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Today's Metrics</h2>
        <div className="text-center py-8 text-gray-500">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-2" />
          <p>Loading metrics...</p>
        </div>
      </div>
    );
  }

  const metrics = [
    {
      id: 'trucks_received',
      title: 'Trucks Received',
      value: data.trucks_received || 0,
      target: data.daily_target || 250,
      icon: Truck,
      color: 'blue',
      change: data.trucks_change || 0,
      unit: '',
    },
    {
      id: 'trucks_completed',
      title: 'Completed',
      value: data.trucks_completed || 0,
      target: data.trucks_received || 0,
      icon: CheckCircle,
      color: 'green',
      change: data.completed_change || 0,
      unit: '',
    },
    {
      id: 'materials_received',
      title: 'Materials Received',
      value: data.materials_received || 0,
      target: null,
      icon: Package,
      color: 'purple',
      change: data.materials_change || 0,
      unit: ' items',
    },
    {
      id: 'sla_breaches',
      title: 'SLA Breaches',
      value: data.sla_breaches || 0,
      target: 0,
      icon: AlertTriangle,
      color: 'red',
      change: data.sla_breaches_change || 0,
      unit: '',
      inverted: true, // Lower is better
    },
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: {
        bg: 'bg-blue-50',
        icon: 'text-blue-600',
        text: 'text-blue-800',
        progress: 'bg-blue-500',
      },
      green: {
        bg: 'bg-green-50',
        icon: 'text-green-600',
        text: 'text-green-800',
        progress: 'bg-green-500',
      },
      purple: {
        bg: 'bg-purple-50',
        icon: 'text-purple-600',
        text: 'text-purple-800',
        progress: 'bg-purple-500',
      },
      red: {
        bg: 'bg-red-50',
        icon: 'text-red-600',
        text: 'text-red-800',
        progress: 'bg-red-500',
      },
    };
    return colors[color] || colors.blue;
  };

  const getTrendIcon = (change, inverted = false) => {
    const isPositive = inverted ? change < 0 : change > 0;
    return isPositive ? TrendingUp : TrendingDown;
  };

  const getTrendColor = (change, inverted = false) => {
    if (change === 0) return 'text-gray-500';
    const isPositive = inverted ? change < 0 : change > 0;
    return isPositive ? 'text-green-600' : 'text-red-600';
  };

  const formatChange = (change) => {
    if (change === 0) return '0%';
    const sign = change > 0 ? '+' : '';
    return `${sign}${change}%`;
  };

  const calculateProgress = (value, target) => {
    if (!target || target === 0) return 0;
    return Math.min((value / target) * 100, 100);
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h2 className={`font-bold text-gray-800 mb-6 ${isFullScreen ? 'text-lg' : 'text-xl'}`}>
        Today's Performance Metrics
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metrics.map((metric) => {
          const colors = getColorClasses(metric.color);
          const Icon = metric.icon;
          const TrendIcon = getTrendIcon(metric.change, metric.inverted);
          const trendColor = getTrendColor(metric.change, metric.inverted);
          const progress = metric.target ? calculateProgress(metric.value, metric.target) : null;

          return (
            <div
              key={metric.id}
              className={`${colors.bg} rounded-lg p-5 border-l-4 border-${metric.color}-500 hover:shadow-md transition-shadow`}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">
                    {metric.title}
                  </p>
                  <div className="flex items-baseline space-x-2">
                    <span className={`text-3xl font-bold ${colors.text}`}>
                      {metric.value}
                    </span>
                    {metric.unit && (
                      <span className="text-sm text-gray-600">{metric.unit}</span>
                    )}
                  </div>
                </div>
                <Icon className={`w-8 h-8 ${colors.icon}`} />
              </div>

              {/* Target Progress */}
              {metric.target !== null && (
                <div className="mb-3">
                  <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                    <span>Target: {metric.target}</span>
                    <span>{Math.round(progress)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`${colors.progress} h-2 rounded-full transition-all duration-500`}
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Trend */}
              <div className={`flex items-center space-x-1 text-sm font-semibold ${trendColor}`}>
                <TrendIcon className="w-4 h-4" />
                <span>{formatChange(metric.change)} vs yesterday</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Additional Stats Row */}
      {data.avg_processing_time && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-gray-800">
                {Math.round(data.avg_processing_time || 0)}m
              </div>
              <div className="text-sm text-gray-600">Avg Processing Time</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-800">
                {data.active_trucks || 0}
              </div>
              <div className="text-sm text-gray-600">Active Trucks</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-800">
                {Math.round(data.qc_pass_rate || 0)}%
              </div>
              <div className="text-sm text-gray-600">QC Pass Rate</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-800">
                {Math.round(data.dock_utilization || 0)}%
              </div>
              <div className="text-sm text-gray-600">Dock Utilization</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TodaysMetrics;
